import { useContext, useMemo } from 'react'
import LockRoundedIcon from '@mui/icons-material/LockRounded'
import PaletteRoundedIcon from '@mui/icons-material/PaletteRounded'
import SettingsBrightnessRoundedIcon from '@mui/icons-material/SettingsBrightnessRounded'
import { Pressable } from '../Common'
import { useGamification } from '../../context/useGamification'
import { useClientPreferences } from '../../context/useClientPreferences'
import { WorkspaceContext } from '../../context/workspaceContextCore'
import { switchedOffModulesFor } from '../../utils/workspaceModules'
import { useNls } from '../../nls/nlsProvider'
import type { AppTheme } from '../../types/schema'
import { DEFAULT_UNLOCKS, RARITY_UNLOCK_LEVEL } from '../../utils/cosmeticCatalog'
import { THEME_CATALOG, type ThemeCatalogEntry } from '../../utils/themeCatalog'

const isUnlocked = (
  entry: ThemeCatalogEntry,
  unlockedIds: Set<string>,
  level: number,
): boolean => {
  if (!entry.cosmeticId) return true
  if (unlockedIds.has(entry.cosmeticId)) return true
  if (entry.rarity && level >= RARITY_UNLOCK_LEVEL[entry.rarity]) return true
  return false
}

export const MemberAppearanceSection = () => {
  const { t } = useNls()
  const { preferences, updatePreferences } = useClientPreferences()
  const { profile: gamificationProfile } = useGamification()
  const workspaceContext = useContext(WorkspaceContext)
  const activeWorkspace = workspaceContext?.workspaces.find((w) => w.id === workspaceContext.activeWorkspaceId) ?? null
  const funPackOff = activeWorkspace ? switchedOffModulesFor(activeWorkspace).has('funPack') : false
  const visibleThemes = useMemo(
    () => (funPackOff ? THEME_CATALOG.filter((t) => !t.cosmeticId) : THEME_CATALOG),
    [funPackOff],
  )
  const level = gamificationProfile?.level ?? 1
  const theme = preferences.memberAppTheme
  const setTheme = (nextTheme: AppTheme | null) => updatePreferences({ memberAppTheme: nextTheme })

  const unlockedIds = useMemo(
    () => new Set<string>([
      ...DEFAULT_UNLOCKS.themes,
      ...(gamificationProfile?.unlockedThemeIds ?? []),
    ]),
    [gamificationProfile?.unlockedThemeIds],
  )

  return (
    <section className="memberAppearanceCard">
      <header className="memberAppearanceHeader">
        <div className="memberAppearanceHeaderIcon">
          <PaletteRoundedIcon fontSize="small" />
        </div>
        <div>
          <span className="memberEyebrow">Appearance</span>
          <h3>Member app theme</h3>
          <p>
            Pick how the Member app looks from central User Settings.
            Locked themes still unlock through your Character vault.
          </p>
        </div>
      </header>

      <div className="memberAppearanceThemeGrid">
        <Pressable
          aria-pressed={theme === null}
          className={`memberAppearanceThemeCard${theme === null ? ' is-active' : ''}`}
          onClick={() => setTheme(null)}
        >
          <span
            className="memberAppearanceSwatch memberAppearanceSwatchFollow"
            aria-hidden="true"
          >
            <SettingsBrightnessRoundedIcon fontSize="inherit" />
          </span>
          <span className="memberAppearanceThemeMeta">
            <strong>Follow console theme</strong>
            <small>Match whatever you picked on the Console.</small>
          </span>
          {theme === null ? <span className="memberAppearanceActiveTag">Active</span> : null}
        </Pressable>

        {visibleThemes.map((entry) => {
          const unlocked = isUnlocked(entry, unlockedIds, level)
          const isActive = theme === entry.key
          return (
            <Pressable
              key={entry.key}
              aria-pressed={isActive}
              className={`memberAppearanceThemeCard${isActive ? ' is-active' : ''}${unlocked ? '' : ' is-locked'}`}
              data-rarity={entry.rarity ?? 'BASE'}
              disabled={!unlocked}
              onClick={() => unlocked && setTheme(entry.key)}
              title={
                unlocked
                  ? entry.description
                  : `Locked — reach Lv ${entry.rarity ? RARITY_UNLOCK_LEVEL[entry.rarity] : '?'} in the Character vault.`
              }
            >
              <span
                className="memberAppearanceSwatch"
                aria-hidden="true"
                style={{
                  background: `radial-gradient(80% 90% at 20% 20%, ${entry.swatch.highlight}, transparent 55%), radial-gradient(80% 90% at 90% 90%, ${entry.swatch.accent}, transparent 60%), ${entry.swatch.surface}`,
                }}
              />
              <span className="memberAppearanceThemeMeta">
                <strong>{entry.label}</strong>
                <small>{entry.description}</small>
              </span>
              {entry.rarity ? (
                <span className="memberAppearanceRarity" data-rarity={entry.rarity}>
                  {entry.rarity.toLowerCase()}
                </span>
              ) : null}
              {!unlocked ? (
                <span className="memberAppearanceLock">
                  <LockRoundedIcon fontSize="inherit" /> Lv {entry.rarity ? RARITY_UNLOCK_LEVEL[entry.rarity] : '?'}
                </span>
              ) : null}
              {isActive && unlocked ? <span className="memberAppearanceActiveTag">Active</span> : null}
            </Pressable>
          )
        })}
      </div>

      <div className="settingsControlPanel">
        <span>{t('NLSStatusMarkersLabel')}</span>
        <small className="settingsFieldHint">
          {t('NLSStatusMarkersHelp')}
        </small>
        <div className="settingsSegmented">
          <Pressable
            aria-pressed={preferences.statusMarkers === 'SHORT'}
            className={preferences.statusMarkers === 'SHORT' ? 'isActive' : ''}
            onClick={() => updatePreferences({ statusMarkers: 'SHORT' })}
          >
            {t('NLSStatusMarkersOptionShort')}
          </Pressable>
          <Pressable
            aria-pressed={preferences.statusMarkers === 'LONG'}
            className={preferences.statusMarkers === 'LONG' ? 'isActive' : ''}
            onClick={() => updatePreferences({ statusMarkers: 'LONG' })}
          >
            {t('NLSStatusMarkersOptionLong')}
          </Pressable>
        </div>
      </div>

      <div className="settingsControlPanel">
        <span>{t('NLSLoadingStyleLabel')}</span>
        <small className="settingsFieldHint">
          {t('NLSLoadingStyleHelp')}
        </small>
        <div className="settingsSegmented">
          <Pressable
            aria-pressed={preferences.loadingStyle === 'CONSTELLATION'}
            className={preferences.loadingStyle === 'CONSTELLATION' ? 'isActive' : ''}
            onClick={() => updatePreferences({ loadingStyle: 'CONSTELLATION' })}
          >
            {t('NLSLoadingStyleOptionConstellation')}
          </Pressable>
          <Pressable
            aria-pressed={preferences.loadingStyle === 'STARFIELD'}
            className={preferences.loadingStyle === 'STARFIELD' ? 'isActive' : ''}
            onClick={() => updatePreferences({ loadingStyle: 'STARFIELD' })}
          >
            {t('NLSLoadingStyleOptionStarfield')}
          </Pressable>
        </div>
      </div>

      <p className="memberAppearanceFootnote">
        This choice lives in User Settings - Appearance. Follow console theme keeps the Member app aligned with your Console theme.
      </p>
    </section>
  )
}
